import defaultSettings from '@/settings';
import * as types from '@/store/mutation-types';
import axios from 'axios';
import {isNull} from 'lodash';
import {EventBus} from '@/plugins/eventBus';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import {push} from 'notivue'
import {handleApiError} from '@/utils/errorHandler.ts';

const {showSettings, tagsView, fixedHeader, sidebarLogo} = defaultSettings;

export interface NotificationSettings {
    fw_booking_email: boolean | null;
    fw_booking_sms: boolean | null;
    getaround_booking_email: boolean | null;
    getaround_booking_sms: boolean | null;
    getaround_communication_error_email: boolean | null;
    getaround_communication_error_sms: boolean | null;
    id: boolean | null;
    tesla_communication_error_email: boolean | null;
    tesla_communication_error_sms: boolean | null;
    turo_booking_email: boolean | null;
    turo_booking_sms: boolean | null;
    turo_communication_error_email: boolean | null;
    turo_communication_error_sms: boolean | null;
    vehicle_maintenance_reminder_sms: boolean | null;
    vehicle_maintenance_reminder_email: boolean | null;
    renter_invoice_paid_sms: boolean | null;
    renter_invoice_paid_email: boolean | null;
    toll_new_transaction_sms: boolean | null;
    toll_new_transaction_email: boolean | null;
    vehicle_recall_sms: boolean | null;
    vehicle_recall_email: boolean | null;
    notify_email: boolean | null;
    notify_sms: boolean | null;
    set: boolean | null;
}

export interface SettingsState {
    theme: any[];
    showSettings: boolean;
    tagsView: boolean;
    fixedHeader: boolean;
    sidebarLogo: boolean;
    loading: Record<string, boolean>;
    notifications: NotificationSettings;
    exportFields: ExportField[],
}

export interface ExportField {
    default: boolean;
    id: string;
    label: string;
    custom?: boolean;
}

const state: SettingsState = {
    theme: [],
    showSettings,
    tagsView,
    fixedHeader,
    sidebarLogo,
    loading: {
        notifications: false,
        settings: false,
        get_settings: false,
    },
    notifications: {
        set: false,
        fw_booking_email: null,
        fw_booking_sms: null,
        getaround_booking_email: null,
        getaround_booking_sms: null,
        getaround_communication_error_email: null,
        getaround_communication_error_sms: null,
        id: null,
        tesla_communication_error_email: null,
        tesla_communication_error_sms: null,
        turo_booking_email: null,
        turo_booking_sms: null,
        turo_communication_error_email: null,
        turo_communication_error_sms: null,
        vehicle_maintenance_reminder_sms: null,
        vehicle_maintenance_reminder_email: null,
        renter_invoice_paid_sms: null,
        renter_invoice_paid_email: null,
        toll_new_transaction_sms: null,
        toll_new_transaction_email: null,
        vehicle_recall_sms: null,
        vehicle_recall_email: null,
        notify_email: null,
        notify_sms: null,
    },
    exportFields: [],
};

const getters = {
    loading: (state: SettingsState) => state.loading,
    needTagsView: (state: SettingsState) => state.tagsView,
    fixedHeader: (state: SettingsState) => state.fixedHeader,
    showSettings: (state: SettingsState) => state.showSettings,
    notifications: (state: SettingsState) => state.notifications,
    exportFields: (state: SettingsState) => state.exportFields,
};

const mutations = {
    [types.SET_SETTINGS_LOADING](state: SettingsState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_NOTIFICATION_SETTINGS](state: SettingsState, settings: NotificationSettings) {
        if (!isNull(settings)) {
            state.notifications = settings;
            state.notifications.set = true;
        }
    },
    [types.SET_EXPORT_FIELDS](state: SettingsState, fields: ExportField[]) {
        state.exportFields = fields;
    },
};

const actions = {
    getSettings({commit}) {
        commit('SET_SETTINGS_LOADING', {subject: 'get_settings', loading: true});

        axios.get('/v1/settings')
            .then((response) => {
                EventBus.$emit('get-settings-success', response.data.settings);
                commit('company/SET_COMPANY_SETTINGS', response.data.settings, {root: true});
            }).catch((error) => {
            push.error({
                message: error?.response?.data?.message || 'Error getting settings',
            });
        }).finally(() => {
            commit('SET_SETTINGS_LOADING', {subject: 'get_settings', loading: false});
        });
    },

    async updateSettings({commit}, data: { settings: { [key: string]: string } }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SETTINGS_LOADING', 'settings', () => axios.post('/v1/settings', data));
            commit('company/SET_COMPANY_SETTINGS', responseData.settings, {root: true});
        } catch (error) {
            handleApiError(error, 'Error updating settings');
        }
    },

    async updateNotificationSettings({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SETTINGS_LOADING', 'notifications', () => axios.put('/v1/settings/notifications', state.notifications));
            push.success({
                message: 'Settings updated successfully!'
            });
            return responseData;
        } catch (error) {
            handleApiError(error, 'Error updating notification settings');
        }
    },

    resetNotificationSettings({commit}) {
        commit('SET_SETTINGS_LOADING', {subject: 'notifications', loading: true});
        axios.post('/v1/settings/notifications/reset')
            .then((response) => {
                commit('SET_NOTIFICATION_SETTINGS', response?.data?.company?.notification_settings);
            }).catch(() => {
        }).finally(() => {
            commit('SET_SETTINGS_LOADING', {subject: 'notifications', loading: false});
        });
    },


    async getExportDetails({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SETTINGS_LOADING', 'export_details', () => axios.get('/v1/exports/details'));
            commit(types.SET_EXPORT_FIELDS, responseData.fields);
        } catch (error) {
            handleApiError(error, 'Error getting export details');
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
